import React from 'react';

import {Filter, FILTERS, MultiValueFilter, Operator, SingleValueFilter} from 'search/SearchConstants';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import pickDefined from 'util/pickDefined';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import ItemSearchInputFilterForm, {ItemSearchInputParentProps} from './base/ItemSearchInputFilterForm';

interface BaseProps<TItem extends Item> extends BaseFilterFormProps {
  filter: Filter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: Filter) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  operators: Array<Operator>;
  itemSearchInputClass:
    | React.ComponentClass<ItemSearchInputParentProps<TItem>>
    | React.FunctionComponent<ItemSearchInputParentProps<TItem>>;
  itemSearchInputExtraProps?: object;
}

interface ItemSearchFilterFormProps<TItem extends Item> extends BaseProps<TItem> {
  onChangeValue: (items: Array<TItem>) => void;
  value: Array<TItem>;
}

function ItemSearchFilterForm<TItem extends Item>(props: ItemSearchFilterFormProps<TItem>): JSX.Element {
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  return (
    <ItemSearchInputFilterForm
      {...skeletonProps}
      filterStyle={props.filterStyle}
      itemSearchInputClass={props.itemSearchInputClass}
      itemSearchInputExtraProps={props.itemSearchInputExtraProps}
      onChangeOperator={(operator: Operator) => {
        props.onChangeFilter({...props.filter, operator});
      }}
      onChangeValue={props.onChangeValue}
      onEnter={props.onEnter}
      operators={props.operators}
      onRemove={props.onRemove}
      selectedOperator={props.filter.operator}
      value={props.value}
      dropdownToggleText={FILTERS[props.filter.key].name!}
    />
  );
}

interface ItemSearchMultiValueFilterFormProps<TItem extends Item> extends BaseProps<TItem> {
  filter: MultiValueFilter;
  onChangeFilter: (filter: MultiValueFilter) => void;
}

export function ItemSearchMultiValueFilterForm<TItem extends Item>(
  props: ItemSearchMultiValueFilterFormProps<TItem>
): JSX.Element {
  return (
    <ItemSearchFilterForm
      {...props}
      onChangeValue={(items) => {
        props.onChangeFilter({...props.filter, value: items.map((item) => item.id!)});
      }}
      value={props.filter.value.map((id) => ({id}) as TItem)}
    />
  );
}

interface ItemSearchSingleValueFilterFormProps<TItem extends Item> extends BaseProps<TItem> {
  filter: SingleValueFilter;
  onChangeFilter: (filter: SingleValueFilter) => void;
}

export function ItemSearchSingleValueFilterForm<TItem extends Item>(
  props: ItemSearchSingleValueFilterFormProps<TItem>
): JSX.Element {
  return (
    <ItemSearchFilterForm
      {...props}
      onChangeValue={(items) => {
        props.onChangeFilter({...props.filter, value: items[0]?.id ?? null});
      }}
      value={props.filter.value ? [{id: props.filter.value} as TItem] : []}
    />
  );
}
